import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Schedule,
  PlayArrow,
  ExpandMore,
  ExpandLess,
  Description,
  Web,
  PictureAsPdf,
  Cancel
} from '@mui/icons-material';
import axios from 'axios';

const JobStatusTracker = ({ onJobComplete }) => {
  const [jobs, setJobs] = useState([]);
  const [expandedJob, setExpandedJob] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/knowledge/jobs/`);
      setJobs(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const cancelJob = async (jobId) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/knowledge/async-jobs/${jobId}/cancel`);
      // Aktualisiere die Jobs nach dem Abbruch
      await fetchJobs();
    } catch (error) {
      console.error('Fehler beim Abbrechen des Jobs:', error);
    }
  };

  useEffect(() => {
    fetchJobs();
    
    // Polling alle 5 Sekunden für laufende Jobs
    const interval = setInterval(() => {
      const hasRunningJobs = jobs.some(job => job.status === 'processing' || job.status === 'pending');
      if (hasRunningJobs) {
        fetchJobs();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [jobs]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'failed':
        return <Error color="error" />;
      case 'cancelled':
        return <Cancel color="error" />;
      case 'processing':
        return <PlayArrow color="primary" />;
      case 'pending':
        return <Schedule color="warning" />;
      default:
        return <Schedule />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'error';
      case 'processing':
        return 'primary';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('de-DE');
  };

  const handleJobComplete = (job) => {
    if (job.status === 'completed' && onJobComplete) {
      onJobComplete(job);
    }
  };

  // Benachrichtige über abgeschlossene Jobs
  useEffect(() => {
    jobs.forEach(job => {
      if (job.status === 'completed') {
        handleJobComplete(job);
      }
    });
  }, [jobs]);

  const toggleExpanded = (jobId) => {
    setExpandedJob(expandedJob === jobId ? null : jobId);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Asynchrone Jobs
      </Typography>
      
      {loading && <LinearProgress />}
      
      {jobs.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="textSecondary">
              Keine asynchronen Jobs vorhanden
            </Typography>
          </CardContent>
        </Card>
      ) : (
        jobs.map((job) => (
          <Card key={job.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" flex={1}>
                  {getStatusIcon(job.status)}
                  <Box ml={2} flex={1}>
                    <Typography variant="h6" component="div">
                      {job.title}
                    </Typography>
                    <Typography color="textSecondary" variant="body2">
                      {job.url}
                    </Typography>
                    <Box display="flex" alignItems="center" mt={1}>
                      <Chip
                        label={job.status}
                        color={getStatusColor(job.status)}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="body2" color="textSecondary">
                        {job.current_step}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center">
                  {(job.status === 'processing' || job.status === 'pending') && (
                    <IconButton 
                      onClick={() => cancelJob(job.id)}
                      color="error"
                      title="Job abbrechen"
                      sx={{ mr: 1 }}
                    >
                      <Cancel />
                    </IconButton>
                  )}
                  <IconButton onClick={() => toggleExpanded(job.id)}>
                    {expandedJob === job.id ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
              </Box>
              
              {job.status === 'processing' && (
                <Box mt={2}>
                  <LinearProgress 
                    variant="determinate" 
                    value={job.progress} 
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {job.progress}% abgeschlossen
                  </Typography>
                </Box>
              )}
              
              <Collapse in={expandedJob === job.id}>
                <Divider sx={{ my: 2 }} />
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Web />
                    </ListItemIcon>
                    <ListItemText
                      primary="Job-Typ"
                      secondary={job.job_type}
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <Description />
                    </ListItemIcon>
                    <ListItemText
                      primary="Seiten"
                      secondary={`${job.processed_pages} von ${job.total_pages} verarbeitet`}
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <PictureAsPdf />
                    </ListItemIcon>
                    <ListItemText
                      primary="PDFs"
                      secondary={`${job.pdfs_processed} von ${job.pdfs_found} verarbeitet`}
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemText
                      primary="Erstellt"
                      secondary={formatDate(job.created_at)}
                    />
                  </ListItem>
                  
                  {job.started_at && (
                    <ListItem>
                      <ListItemText
                        primary="Gestartet"
                        secondary={formatDate(job.started_at)}
                      />
                    </ListItem>
                  )}
                  
                  {job.completed_at && (
                    <ListItem>
                      <ListItemText
                        primary="Abgeschlossen"
                        secondary={formatDate(job.completed_at)}
                      />
                    </ListItem>
                  )}
                  
                  <ListItem>
                    <ListItemText
                      primary="Dauer"
                      secondary={formatDuration(job.actual_duration)}
                    />
                  </ListItem>
                  
                  {job.error_message && (
                    <ListItem>
                      <ListItemText
                        primary="Fehler"
                        secondary={job.error_message}
                        secondaryTypographyProps={{ color: 'error' }}
                      />
                    </ListItem>
                  )}
                  
                  {job.knowledge_base_id && (
                    <ListItem>
                      <ListItemText
                        primary="Wissensdatenbank ID"
                        secondary={job.knowledge_base_id}
                      />
                    </ListItem>
                  )}
                </List>
              </Collapse>
            </CardContent>
          </Card>
        ))
      )}
    </Box>
  );
};

export default JobStatusTracker;

